<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { ContractManagementCalendarLinkResult } from '@/api';
import { ContractViewsService } from '@/services/contractManagement/contractViewsService';
import { logger } from '@/utils/logger';
import GaiaBanner from '../../composite/GaiaBanner.vue';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaModal from '../../base/GaiaModal.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaSpinner from '../../base/GaiaSpinner.vue';
import GaiaVaultPicker from '../fileVault/GaiaVaultPicker.vue';

/**
 * Edit which folders and contracts a saved view covers. Loads the view's
 * current picks on open and, on save, sends only the difference. Picking
 * nothing is allowed: the view then covers every contract in the main vault.
 */
const props = defineProps<{ viewUid: string }>();

const show = defineModel<boolean>('show', { required: true });

const emit = defineEmits<{
  /** The scope changed on the server — the grid should reload. */
  saved: [];
}>();

const directoryUids = ref<string[]>([]);
const contractUids = ref<string[]>([]);
/** What the server had when the modal opened, to diff against. */
const pristine = ref<{ directoryUids: string[]; contractUids: string[] }>({
  directoryUids: [],
  contractUids: [],
});

const loading = ref<boolean>(false);
const loadError = ref<string | null>(null);
const saving = ref<boolean>(false);
const saveProblems = ref<string[]>([]);

async function load(): Promise<void> {
  loading.value = true;
  loadError.value = null;
  try {
    const [directories, contracts] = await Promise.all([
      ContractViewsService.listSourceDirectories(props.viewUid),
      ContractViewsService.listSourceContracts(props.viewUid),
    ]);
    pristine.value = {
      directoryUids: directories.map(directory => directory.uid),
      contractUids: contracts.map(contract => contract.uid),
    };
    directoryUids.value = [...pristine.value.directoryUids];
    contractUids.value = [...pristine.value.contractUids];
  } catch (error) {
    logger.error('Error loading view scope:', error);
    loadError.value = error instanceof Error ? error.message : 'Failed to load the view’s scope';
  } finally {
    loading.value = false;
  }
}

watch(show, open => {
  if (!open) return;
  saveProblems.value = [];
  void load();
});

function diffUids(previous: string[], next: string[]): { added: string[]; removed: string[] } {
  return {
    added: next.filter(uid => !previous.includes(uid)),
    removed: previous.filter(uid => !next.includes(uid)),
  };
}

const directoryDiff = computed(() => diffUids(pristine.value.directoryUids, directoryUids.value));
const contractDiff = computed(() => diffUids(pristine.value.contractUids, contractUids.value));

const isDirty = computed(
  () =>
    directoryDiff.value.added.length > 0 ||
    directoryDiff.value.removed.length > 0 ||
    contractDiff.value.added.length > 0 ||
    contractDiff.value.removed.length > 0
);

const summary = computed(() => {
  const parts = (
    [
      [directoryUids.value.length, 'folder'],
      [contractUids.value.length, 'contract'],
    ] as const
  )
    .filter(([count]) => count > 0)
    .map(([count, noun]) => `${count} ${noun}${count === 1 ? '' : 's'}`);
  return parts.length > 0
    ? `Selected: ${parts.join(' · ')}`
    : 'Nothing selected — the view covers every contract in the vault.';
});

/**
 * Every call is attempted even if one fails — folders and contracts are
 * independent — and the per-uid errors are what the user sees.
 */
async function save(): Promise<void> {
  if (saving.value || !isDirty.value) return;
  saving.value = true;
  saveProblems.value = [];

  const uid = props.viewUid;
  const calls: {
    what: string;
    run: () => Promise<ContractManagementCalendarLinkResult>;
  }[] = [];
  const { added: addDirs, removed: removeDirs } = directoryDiff.value;
  const { added: addContracts, removed: removeContracts } = contractDiff.value;
  if (addDirs.length > 0) {
    calls.push({
      what: 'add folders',
      run: () => ContractViewsService.addSourceDirectories(uid, addDirs),
    });
  }
  if (removeDirs.length > 0) {
    calls.push({
      what: 'remove folders',
      run: () => ContractViewsService.removeSourceDirectories(uid, removeDirs),
    });
  }
  if (addContracts.length > 0) {
    calls.push({
      what: 'add contracts',
      run: () => ContractViewsService.addSourceContracts(uid, addContracts),
    });
  }
  if (removeContracts.length > 0) {
    calls.push({
      what: 'remove contracts',
      run: () => ContractViewsService.removeSourceContracts(uid, removeContracts),
    });
  }

  const outcomes = await Promise.allSettled(calls.map(call => call.run()));
  const problems: string[] = [];
  for (const [index, outcome] of outcomes.entries()) {
    const { what } = calls[index];
    if (outcome.status === 'rejected') {
      logger.error(`Error trying to ${what} on view:`, outcome.reason);
      problems.push(`Could not ${what}.`);
      continue;
    }
    for (const failure of outcome.value.results.filter(item => !item.success)) {
      problems.push(`Could not ${what}: ${failure.error ?? 'unknown error'}`);
    }
  }

  saving.value = false;
  // Something may have gone through even when something else failed.
  emit('saved');
  if (problems.length > 0) {
    // Reload so the picker shows what the view now really covers.
    saveProblems.value = problems;
    await load();
    return;
  }
  show.value = false;
}
</script>

<template>
  <gaia-modal
    v-model:show="show"
    width="880px"
    title="Contracts in this view"
    :mask-closable="!saving"
    :close-on-esc="!saving"
  >
    <n-flex vertical :size="12" :wrap="false">
      <gaia-text size="body" tone="text">
        A folder includes every contract in it and its subfolders, including ones added later. A
        contract is included on its own. Pick nothing to cover every contract in the vault.
      </gaia-text>

      <gaia-banner v-if="saveProblems.length > 0" type="error">
        <n-flex vertical :size="2" :wrap="false">
          <gaia-text v-for="problem in saveProblems" :key="problem" size="body">
            {{ problem }}
          </gaia-text>
        </n-flex>
      </gaia-banner>

      <gaia-banner v-if="loadError" type="error">{{ loadError }}</gaia-banner>
      <n-flex v-else-if="loading" justify="center" :size="0" :wrap="false">
        <gaia-spinner :size="20" />
      </n-flex>
      <template v-else>
        <gaia-vault-picker
          v-model:directory-uids="directoryUids"
          v-model:contract-uids="contractUids"
        />
        <gaia-text size="meta" tone="subtle">{{ summary }}</gaia-text>
      </template>
    </n-flex>

    <template #action>
      <n-flex justify="end" :size="8" :wrap="false">
        <gaia-button :disabled="saving" @click="show = false"> Cancel </gaia-button>
        <gaia-button
          type="primary"
          :loading="saving"
          :disabled="loading || !!loadError || !isDirty"
          @click="save"
        >
          Save
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>
